import { useDebounceCallback } from "@react-hook/debounce";
import { pipe } from "fp";
import React from "react";

import { type, creator, reducer, useDispatch } from "~/Action";
import { Input } from "~/Input";
import { useSelector } from "~/State";
import { Time } from "~/Time";

export type Search = string;

const delay = (): Time.Duration => Time.Duration.milliseconds(400);

export const matches = (text: string, search: Search): boolean =>
  text.toLocaleLowerCase().includes(search.toLocaleLowerCase());

export const useSearch = (): Search =>
  useSelector(({ elds: { search } }) => search);

export const Component: React.Fn = () =>
  pipe(
    useDispatch(),
    (dispatch) =>
      // Debounced on the *value*, not the event. React 17 pools synthetic events
      // and nulls their fields once the handler returns, so an event read after
      // the debounce delay would be empty — which is what `event.persist()` was
      // working around before this dispatched at all.
      useDebounceCallback(
        (search: string) => dispatch(Action.Updated.create(search)),
        delay().duration
      ),
    (onSearch) => (
      <Input
        style={{ width: 200 }}
        placeholder="Search submissions"
        onChange={(event) => onSearch(event.target.value)}
      />
    )
  );

export namespace State {
  export type State = Search;
  export const initial = (): State => "";
}

export namespace Action {
  export namespace Updated {
    export const { TYPE, create, reduce } = pipe(
      type("ELDs updated search" as const),
      creator((search: string) => ({ search })),
      reducer(({ state, action: { search } }) => (state.elds.search = search))
    );
  }
}
